import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Radio, Calendar, User, Users, ArrowLeft, ShieldCheck, Loader2, PlayCircle, CheckCircle2 } from "lucide-react";
import { api, apiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const fmt = (iso) => { try { return new Date(iso).toLocaleString("fr-FR", { dateStyle: "full", timeStyle: "short" }); } catch { return ""; } };
const OPERATORS = ["Orange Money", "MTN MoMo", "Moov Money", "Wave"];

function ReplayPlayer({ w }) {
  const url = w.replay_url || "";
  const yt = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([\w-]{11})/);
  if (w.replay_type === "mp4") return <video src={url} controls className="w-full rounded-xl bg-black" data-testid="webinar-replay-video" />;
  if (yt) return <div className="aspect-video w-full overflow-hidden rounded-xl"><iframe title="replay" src={`https://www.youtube.com/embed/${yt[1]}`} className="h-full w-full" allowFullScreen data-testid="webinar-replay-embed" /></div>;
  return <a href={url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-ciel px-5 py-3 text-sm font-semibold text-white" data-testid="webinar-replay-link"><PlayCircle size={16} /> Ouvrir le replay</a>;
}

export default function WebinaireEvent() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [w, setW] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const [pay, setPay] = useState(null); // {registration_id, amount_label}
  const [operator, setOperator] = useState("Orange Money");
  const [done, setDone] = useState(null);

  const load = () => api.get(`/webinars/${id}`).then((r) => setW(r.data)).catch((e) => { toast.error(apiError(e)); navigate("/webinaires"); });
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  const submit = async () => {
    if (!form.name.trim() || !form.email.trim()) { toast.error("Nom et email requis"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/webinars/${id}/register`, form);
      if (data.status === "confirmed" || data.status === "already") { setDone(data.token); toast.success("Inscription confirmée !"); }
      else if (data.status === "payment_required") setPay(data);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const payMomo = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/webinars/registrations/${pay.registration_id}/pay/mobile-money`, { operator, phone: form.phone || "0700000000" });
      setDone(data.token); setPay(null); toast.success("Paiement confirmé — inscription validée !");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const payStripe = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/webinars/registrations/${pay.registration_id}/pay/stripe`, { origin_url: window.location.origin, currency: "EUR" });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(apiError(e)); setBusy(false); }
  };

  if (!w) return <div className="flex min-h-[50vh] items-center justify-center"><Loader2 className="animate-spin text-ciel" /></div>;

  return (
    <div className="mx-auto max-w-5xl px-4 py-10" data-testid="webinaire-event">
      <button onClick={() => navigate("/webinaires")} className="mb-6 flex items-center gap-1 text-sm text-slate-500 hover:text-navy"><ArrowLeft size={15} /> Tous les webinaires</button>

      <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white">
        <div className="relative h-56 bg-navy sm:h-72">
          {w.cover_url ? <img src={w.cover_url} alt={w.title} className="h-full w-full object-cover" />
            : <div className="flex h-full items-center justify-center bg-gradient-to-br from-navy to-sky-800"><Radio className="text-white/40" size={56} /></div>}
          <span className={`absolute left-4 top-4 rounded-full px-3 py-1 text-xs font-semibold ${w.price_xof === 0 ? "bg-emerald-500 text-white" : "bg-amber-500 text-white"}`} data-testid="webinar-price">{w.price_label}</span>
          {w.live && <span className="absolute right-4 top-4 flex items-center gap-1.5 rounded-full bg-red-600 px-3 py-1 text-xs font-semibold text-white shadow" data-testid="webinar-live-badge"><span className="h-2 w-2 animate-pulse rounded-full bg-white" /> EN DIRECT</span>}
        </div>
        <div className="p-6 sm:p-8">
          <h1 className="font-display text-3xl font-bold text-navy" data-testid="webinar-title">{w.title}</h1>
          <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-600">
            {w.speaker && <span className="flex items-center gap-1.5"><User size={15} className="text-ciel" /> {w.speaker}{w.speaker_title ? ` · ${w.speaker_title}` : ""}</span>}
            <span className="flex items-center gap-1.5"><Calendar size={15} className="text-ciel" /> {fmt(w.starts_at)}</span>
            {w.remaining !== null && !w.ended && <span className="flex items-center gap-1.5"><Users size={15} className="text-ciel" /> {w.is_full ? "Complet" : `${w.remaining} place(s) restante(s)`}</span>}
          </div>
          {w.description && <p className="mt-5 whitespace-pre-line text-slate-600">{w.description}</p>}

          {w.ended ? (
            w.replay_available ? (
              <div className="mt-6" data-testid="webinar-replay-section">
                <h2 className="mb-3 flex items-center gap-2 font-display text-lg font-semibold text-navy"><PlayCircle size={18} className="text-ciel" /> Replay de la session</h2>
                <ReplayPlayer w={w} />
              </div>
            ) : <p className="mt-6 rounded-xl bg-slate-50 p-4 text-sm text-slate-500">Ce webinaire est terminé. Le replay sera bientôt disponible.</p>
          ) : done ? (
            <div className="mt-6 rounded-2xl border border-emerald-300 bg-emerald-50 p-5" data-testid="webinar-registered">
              <p className="flex items-center gap-2 font-semibold text-emerald-800"><CheckCircle2 size={18} /> Vous êtes inscrit·e !</p>
              <p className="mt-1 text-sm text-emerald-700">Un email de confirmation vous a été envoyé avec votre lien d'accès personnel.</p>
              <Button onClick={() => navigate(`/webinaire/acces/${done}`)} data-testid="webinar-go-access" className="mt-3 rounded-full bg-navy text-white">Voir mon accès</Button>
            </div>
          ) : (
            <Button onClick={() => setOpen(true)} disabled={w.is_full} data-testid="webinar-register-open" className="mt-6 rounded-full bg-ciel px-8 py-6 text-base font-semibold text-white hover:bg-sky-600">
              {w.is_full ? "Complet" : (w.price_xof === 0 ? "S'inscrire gratuitement" : `S'inscrire · ${w.price_label}`)}
            </Button>
          )}
          <p className="mt-4 flex items-center gap-1 text-xs text-slate-400"><ShieldCheck size={12} /> Cabinet CARIS Santé — Côte d'Ivoire. Inscription sécurisée.</p>
        </div>
      </div>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setPay(null); }}>
        <DialogContent data-testid="webinar-register-dialog">
          <DialogHeader><DialogTitle className="font-display text-navy">{pay ? "Paiement de l'inscription" : "Inscription au webinaire"}</DialogTitle>
            <DialogDescription>{pay ? "Réglez votre place pour recevoir votre lien d'accès." : "Renseignez vos coordonnées pour recevoir votre lien de participation."}</DialogDescription>
          </DialogHeader>
          {!pay ? (
            <div className="space-y-3">
              <div><label className="text-xs text-slate-500">Nom complet *</label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" data-testid="webinar-name" /></div>
              <div><label className="text-xs text-slate-500">Email *</label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" data-testid="webinar-email" /></div>
              <div><label className="text-xs text-slate-500">Téléphone (WhatsApp, optionnel)</label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+225…" className="mt-1" data-testid="webinar-phone" /></div>
              <Button onClick={submit} disabled={busy} data-testid="webinar-submit" className="w-full rounded-full bg-ciel text-white hover:bg-sky-600">
                {busy ? <Loader2 size={15} className="animate-spin" /> : (w.price_xof === 0 ? "Confirmer mon inscription" : "Continuer vers le paiement")}
              </Button>
            </div>
          ) : (
            <div className="space-y-4" data-testid="webinar-payment">
              <p className="text-sm text-slate-600">Montant : <strong className="text-navy">{pay.amount_label}</strong></p>
              <div>
                <label className="text-xs text-slate-500">Mobile Money</label>
                <div className="mt-1 flex gap-2">
                  <Select value={operator} onValueChange={setOperator}>
                    <SelectTrigger className="flex-1" data-testid="webinar-operator"><SelectValue /></SelectTrigger>
                    <SelectContent>{OPERATORS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                  </Select>
                  <Button onClick={payMomo} disabled={busy} data-testid="webinar-pay-momo" className="rounded-full bg-emerald-600 text-white hover:bg-emerald-700">Payer</Button>
                </div>
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-400"><span className="h-px flex-1 bg-slate-200" /> ou <span className="h-px flex-1 bg-slate-200" /></div>
              <Button onClick={payStripe} disabled={busy} variant="outline" data-testid="webinar-pay-stripe" className="w-full rounded-full">Payer par carte (Stripe)</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
